import styled, { css } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';

const defaultStyles = css`
  display: inline-flex;
  justify-content: center;
  align-items: center;

  font-family: ${themeGet('fonts.primary')};
  font-weight: ${themeGet('font-weights.semi-bold')};
  text-align: center;
  border-radius: 4px;
  border-width: 3px;
  border-style: solid;
  text-decoration: none;

  font-size: 19px;
  letter-spacing: -0.5px;
  line-height: 21px;

  height: 48px;
  min-height: 48px;
  min-width: 160px;

  padding: 0 22px;

  transition: all 500ms ease;

  &&:disabled {
    cursor: not-allowed;
    border-color: ${themeGet('colors.pale-grey')};
    background-color: ${themeGet('colors.pale-grey')};
    color: ${themeGet('colors.grey')};

    &:hover,
    &:focus {
      cursor: not-allowed;
      border-color: ${themeGet('colors.pale-grey')};
      background-color: ${themeGet('colors.pale-grey')};
    }
  }

  /* Make all buttons 100% width on mobile unless auto-size is set as a className */
  @media only screen and (max-width: calc(${themeGet(
      'breakpoints.md'
    )} - 1px)) {
    width: 100%;

    &.auto-size {
      width: auto;
    }
  }
`;

export const ButtonStyles = css`
  // * this is only used to hide the main header nav CTA
  &.no-cta-button {
    ${defaultStyles}
    border-color: ${themeGet('colors.white')};
    color: none;
    background-color: none;
    border-bottom: none;
  }
  // * Solid Orange -> Solid Darker Orange -> Darkest Orange
  &.solid-on-light-bg {
    ${defaultStyles}
    border-color: ${themeGet('colors.orange')};
    background-color: ${themeGet('colors.orange')};
    color: ${themeGet('colors.white')};

    &:hover,
    &:focus {
      border-color: ${themeGet('colors.dark-orange')};
      background-color: ${themeGet('colors.dark-orange')};
      color: ${themeGet('colors.white')};
    }

    &:focus,
    &:active {
      border-color: ${themeGet('colors.darkest-orange')};
      background-color: ${themeGet('colors.darkest-orange')};
      color: ${themeGet('colors.white')};
    }
  }

  //Solid on light style with custom background color
  &.solid-on-light-custom-bg {
    ${defaultStyles}
    color: ${themeGet('colors.white')};

    &:hover,
    &:focus {
      color: ${themeGet('colors.white')};
    }

    &:focus,
    &:active {
      color: ${themeGet('colors.white')};
    }
  }

  // * Solid Orange -> Solid Darker Orange -> Darkest Orange
  &.solid-on-dark-bg {
    ${defaultStyles}
    border-color: ${themeGet('colors.orange')};
    background-color: ${themeGet('colors.orange')};
    color: ${themeGet('colors.white')};

    &:hover,
    &:focus {
      border-color: ${themeGet('colors.dark-orange')};
      background-color: ${themeGet('colors.dark-orange')};
      color: ${themeGet('colors.white')};
    }

    &:focus,
    &:active {
      border-color: ${themeGet('colors.darkest-orange')};
      background-color: ${themeGet('colors.darkest-orange')};
      color: ${themeGet('colors.white')};
    }
  }

  // * Ghost Orange -> Solid Darker Orange -> Darkest Orange
  &.ghost-on-light-bg {
    ${defaultStyles}
    border-color: ${themeGet('colors.orange')};
    background-color: transparent;
    color: ${themeGet('colors.black')};

    &:hover,
    &:focus {
      color: ${themeGet('colors.white')};
      border-color: ${themeGet('colors.dark-orange')};
      background-color: ${themeGet('colors.dark-orange')};
    }

    &:focus,
    &:active {
      color: ${themeGet('colors.white')};
      border-color: ${themeGet('colors.darkest-orange')};
      background-color: ${themeGet('colors.darkest-orange')};
    }
  }

  // * Ghost White -> Solid Orange
  &.ghost-on-dark-bg {
    ${defaultStyles}
    border-color: ${themeGet('colors.orange')};
    background-color: transparent;
    color: ${themeGet('colors.white')};

    &:hover,
    &:focus {
      border-color: ${themeGet('colors.dark-orange')};
      background-color: ${themeGet('colors.dark-orange')};
      color: ${themeGet('colors.white')};
    }

    &:focus,
    &:active {
      border-color: ${themeGet('colors.darkest-orange')};
      background-color: ${themeGet('colors.darkest-orange')};
      color: ${themeGet('colors.white')};
    }
  }

  // * Solid White -> Solid Grey
  &.brand-solid {
    ${defaultStyles}
    border-color: ${themeGet('colors.white')};
    background-color: ${themeGet('colors.white')};
    color: ${themeGet('colors.black')};

    &:hover,
    &:focus {
      border-color: #f8f7f5;
      background-color: #f8f7f5;
      color: ${themeGet('colors.black')};
    }

    &:focus,
    &:active {
      border-color: #ebeae8;
      background-color: #ebeae8;
      color: ${themeGet('colors.black')};
    }
  }

  // * Ghost White -> Solid Black
  &.brand-ghost {
    ${defaultStyles}
    border-color: ${themeGet('colors.white')};
    background-color: transparent;
    color: ${themeGet('colors.white')};

    &:hover,
    &:focus {
      color: ${themeGet('colors.black')};
      border-color: #f8f7f5;
      background-color: #f8f7f5;
    }

    &:focus,
    &:active {
      color: ${themeGet('colors.black')};
      border-color: #ebeae8;
      background-color: #ebeae8;
    }
  }

  // * Solid Ocean -> Solid Darker Orange -> Darkest Orange
  &.secondary-solid-on-light-bg {
    ${defaultStyles}
    border-color: ${themeGet('colors.ocean')};
    background-color: ${themeGet('colors.ocean')};
    color: ${themeGet('colors.white')};

    &:hover,
    &:focus {
      border-color: ${themeGet('colors.dark-ocean')};
      background-color: ${themeGet('colors.dark-ocean')};
      color: ${themeGet('colors.white')};
    }

    &:focus,
    &:active {
      border-color: ${themeGet('colors.darkest-ocean')};
      background-color: ${themeGet('colors.darkest-ocean')};
      color: ${themeGet('colors.white')};
    }
  }

  // * Black --> Cream
  &.black-on-cream-bg {
    ${defaultStyles}
    border-color: ${themeGet('colors.cream')};
    background-color: ${themeGet('colors.cream')};
    color: ${themeGet('colors.black')};

    &:hover,
    &:focus {
      border-color: ${themeGet('colors.cream')};
      background-color: ${themeGet('colors.cream')};
      color: ${themeGet('colors.black')};
    }

    &:focus,
    &:active {
      border-color: ${themeGet('colors.cream')};
      background-color: ${themeGet('colors.cream')};
      color: ${themeGet('colors.black')};
    }
  }

  // * White --> fuschia
  &.white-on-fuschia-bg {
    ${defaultStyles}
    border-color: ${themeGet('colors.fuschia')};
    background-color: ${themeGet('colors.fuschia')};
    color: ${themeGet('colors.white')};

    &:hover,
    &:focus {
      border-color: ${themeGet('colors.fuschia')};
      background-color: ${themeGet('colors.fuschia')};
      color: ${themeGet('colors.white')};
    }

    &:focus,
    &:active {
      border-color: ${themeGet('colors.fuschia')};
      background-color: ${themeGet('colors.fuschia')};
      color: ${themeGet('colors.white')};
    }
  }

  // * Solid White -> Solid Grey -> align left
  &.brand-solid-align-left {
    ${defaultStyles}
    text-align: left;
    padding: 14px 24px;
    height: auto;
    border-color: ${themeGet('colors.white')};
    background-color: ${themeGet('colors.white')};
    color: ${themeGet('colors.black')};
    margin-top: 0px;
    max-width: 390px;

    &:hover,
    &:focus {
      border-color: #f8f7f5;
      background-color: #f8f7f5;
      color: ${themeGet('colors.black')};
    }

    &:focus,
    &:active {
      border-color: #ebeae8;
      background-color: #ebeae8;
      color: ${themeGet('colors.black')};
    }
  }
`;

export const Btn = styled.button`
  ${ButtonStyles}
`;
